import { useEffect, useRef, useState } from 'react';

import log from 'loglevel';

import { InboxEntrySaveOrigin } from '../../enums/inboxEntrySaveOrigin';
import { useGetDetailedInboxEntry } from '../../services/inboxEntries.service';
import { useInboxEntriesStore } from '../../store';

interface UseLoadEntryDetailOnceOptions {
  /** Namespaces the attempt map so two consumers of the same entry never overwrite each other. */
  scope: string;
  errorLabel: string;
  includeChatActivity?: boolean;
}

// Outlives the drawer: closing and reopening it must not fetch a key again.
const attemptedKeyByScopedEntry = new Map<string, string>();

// One fetch per key: a key the detail cannot fill, or whose fetch failed, is not retried.
// An empty key means there is nothing to load.
export function useLoadEntryDetailOnce(
  entryId: string,
  key: string,
  { scope, errorLabel, includeChatActivity }: UseLoadEntryDetailOnceOptions,
) {
  const { getDetailedInboxEntry } = useGetDetailedInboxEntry({
    includeChatActivity,
  });
  const attemptKey = `${scope}:${entryId}`;
  const [settledKey, setSettledKey] = useState(
    () => attemptedKeyByScopedEntry.get(attemptKey) ?? null,
  );
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!key || attemptedKeyByScopedEntry.get(attemptKey) === key) {
      return;
    }
    attemptedKeyByScopedEntry.set(attemptKey, key);

    const load = async () => {
      try {
        const detailed = await getDetailedInboxEntry(entryId);
        if (detailed) {
          useInboxEntriesStore
            .getState()
            .taskManager.activeTaskGroup.saveDetailedInboxEntry(
              detailed,
              InboxEntrySaveOrigin.ALREADY_COUNTED,
            );
        }
      } catch (error) {
        log.error(errorLabel, error);
      } finally {
        if (isMounted.current) {
          setSettledKey(key);
        }
      }
    };
    void load();
  }, [attemptKey, entryId, key]);

  return { isLoading: !!key && settledKey !== key };
}
